import { Box } from "@mui/material";
import { SAFE_BOTTOM } from "../styles/safeArea";
import { CHROME_INSET } from "../styles/zLayers";
import { dvh } from "../styles/viewportHeight";
import React, { useLayoutEffect, useRef, useState } from "react";
import Footer from "./foooter/Footer";
import TopAccountBar from "./components/TopAccountBar";
import OnboardingCoach from "./components/OnboardingCoach";
import ServerWakeNotice from "./components/ServerWakeNotice";
import { useLocation } from "react-router-dom";
import { usePageViews } from "../analytics/useAnalytics";
import { isAuthRoute, isFullscreenRoute, isImmersiveChatRoute } from "./chromeRoutes";
import { ScrollContainerProvider } from "./ScrollContainerContext";
import { useEscapeToDismiss } from "../native/escapeToDismiss";

const Layout = ({ children }) => {
  const location = useLocation();
  usePageViews();
  // Escape closes the top-most registered overlay, the same one Android's Back
  // does. Here rather than on any one screen: it is a single document listener
  // and a second copy would close two overlays per press. Above the `hideChrome`
  // early return on purpose — auth routes take that branch and still have
  // dismissible dialogs.
  useEscapeToDismiss();
  // The inner Box below — not the document — is what scrolls. Publish it so an
  // in-shell page can read/drive the real scrollport instead of `window`, which
  // never scrolls here (P2-59). A state setter as the callback ref keeps the
  // identity stable and re-renders once, when the element exists.
  const [scrollEl, setScrollEl] = useState(null);
  const hideChrome = isAuthRoute(location.pathname);
  // An open conversation owns the viewport (#149): drop the global top account
  // bar + bottom nav (so the chat header ⋯ never collides with them, and the
  // footer can't wedge between the composer and the Android keyboard), and give
  // the content the same full-height, no-footer-gap treatment as the map.
  const immersiveChat = isImmersiveChatRoute(location.pathname, location.search);
  const fullscreen = isFullscreenRoute(location.pathname) || immersiveChat;

  // A route change swaps `children` under a scrollport that stays the SAME DOM
  // node — App.js renders `<Layout><Router /></Layout>`, so Layout is above the
  // router — and nothing in the shell ever put that node back to the top. So a
  // navigation used to land you at the offset the page you LEFT had scrolled to
  // (#207).
  //
  // Keyed on pathname ONLY. ChatPage mirrors the open thread into ?threadId (see
  // chromeRoutes.js), so a reset keyed on the search string would fire on every
  // thread open and close.
  //
  // ORDERING against a page that wants to own its entry offset is NOT settled
  // here — it turns on whether that page mounts in the SAME commit as the
  // pathname change. It does for the eagerly-imported routes (MapPage,
  // CollectionPage, TradesPage) and for a lazy route whose chunk is already
  // resolved; React flushes child layout effects before parent ones, so there a
  // page's mount useLayoutEffect runs BEFORE this one and this one overwrites
  // it. But most in-shell routes are code-split behind Suspense (Router.jsx,
  // `lazyRoute` at :70 — PokemonMain, ChatPage, WishlistPage, UserProfile,
  // TradeJourneyPage, PostsPage, every admin page), and on a COLD first visit
  // that commit mounts only the fallback: the real page mounts in a LATER commit,
  // so its mount useLayoutEffect runs AFTER this reset and keeps its offset.
  // A page therefore must not lean on either outcome. What it CAN rely on is that
  // a mount useEffect, or anything in a requestAnimationFrame, runs after this and
  // wins in both cases. PokemonMain — the only consumer of this scrollport
  // (useScrollContainer) — restores its per-set offset in a rAF.
  //
  // useLayoutEffect, not useEffect: it runs before paint, so no frame shows the
  // inherited offset. `scrollEl` is a dep because it arrives one render after the
  // shell mounts; the pathname guard (the same shape as usePageViews) makes that
  // extra run a no-op.
  const lastPathRef = useRef(location.pathname);
  useLayoutEffect(() => {
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    if (scrollEl) scrollEl.scrollTop = 0;
  }, [location.pathname, scrollEl]);

  // On every route, auth ones included: signing in is often the first request
  // to hit a sleeping server (B-60).
  if (hideChrome) {
    return (
      <>
        {children}
        <ServerWakeNotice />
      </>
    );
  }

  return (
    <Box
      data-testid="app-layout"
      sx={{
        display: "flex",
        flexDirection: "column",
        // Not a bare "100dvh": a Chrome 90 WebView drops it and the shell grows
        // to its content, so the document scrolls instead of the scrollport.
        height: dvh(100),
        width: "100%",
        maxWidth: 520,
        mx: "auto",
        overflow: "hidden",
        boxShadow: { xs: "none", sm: "0 0 40px rgba(15,23,42,0.08)" },
      }}
    >
      {!immersiveChat && <TopAccountBar />}
      <Box
        ref={setScrollEl}
        data-testid="app-scrollport"
        // Hook for index.css's short-window rule (B-40), which shrinks this
        // reserve along with the compact bottom nav. Fullscreen routes have no
        // reserve to shrink.
        className={fullscreen ? undefined : "tl-scrollport"}
        sx={{
          flex: 1,
          overflowY: fullscreen ? "hidden" : "auto",
          // The footer is `position: fixed; bottom: 0`, so this reserve is the
          // only thing keeping the last rows of a list out from under it. It
          // was a hand-typed 80px against a footer that measures 97px on the
          // rig at 375x812 — the bottom 17px of every scrolling route could
          // not be brought into view at maximum scroll — and HEAD's safe-area
          // fix then grew the footer by the inset without moving this, taking
          // the shortfall to 65px on a 48px system bar. Same terms as the
          // footer's own lift now, so the two move together.
          pb: fullscreen ? 0 : `calc(${CHROME_INSET.bottom + 8}px + ${SAFE_BOTTOM})`,
          // No phantom header offset — chrome is floating top-right + bottom footer only.
          height: fullscreen ? dvh(100) : "auto",
          minHeight: fullscreen ? 300 : undefined,
        }}
      >
        <ScrollContainerProvider value={scrollEl}>{children}</ScrollContainerProvider>
      </Box>
      {/* The scrollport, so a re-tap on the current tab can scroll it to the top (B-70). */}
      {!immersiveChat && <Footer scrollEl={scrollEl} />}
      <OnboardingCoach />
      <ServerWakeNotice />
    </Box>
  );
};

export default Layout;
